import {
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Button,
  Grid,
  Avatar,
  Divider,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDButton from "theme/components/MDButton";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function WhatsappMessagePoolPage() {
  const pageTitle = "WhatsApp - Incoming Message Pool";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [messages, setMessages] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState({});

  const [assigning, setAssigning] = useState(null);

  const getPayload = (payload) => {
    if (!payload) return {};

    if (typeof payload === "object") {
      return payload;
    }

    if (typeof payload === "string") {
      try {
        return JSON.parse(payload);
      } catch (error) {
        return { text: payload };
      }
    }

    return {};
  };

  const getMessageDetails = (messagePayload) => {
    const payload = getPayload(messagePayload);

    let message = payload;

    if (payload.entry && payload.entry.length > 0) {
      const changes = payload.entry[0]?.changes || [];

      message = changes[0]?.value?.messages?.[0] || changes[0]?.value || payload;
    } else if (payload.messages && payload.messages.length > 0) {
      message = payload.messages[0];
    }

    const type = message.type || "text";
    let preview = "New message";

    switch (type) {
      case "text":
        preview = message.text?.body || "Text message";
        break;

      case "image":
        preview = message.image?.caption || "Image";
        break;

      case "video":
        preview = message.video?.caption || "Video";
        break;

      case "audio":
        preview = "Audio message";
        break;

      case "document":
        preview = message.document?.filename || message.document?.caption || "Document";
        break;

      case "sticker":
        preview = "Sticker";
        break;

      case "location":
        preview = "Location";
        break;

      case "contacts":
        preview = "Contact card";
        break;

      case "button":
        preview = message.button?.text || "Button response";
        break;

      case "interactive":
        preview =
          message.interactive?.button_reply?.title ||
          message.interactive?.list_reply?.title ||
          "Interactive response";
        break;

      default:
        preview = `${type.charAt(0).toUpperCase()}${type.slice(1)} message`;
    }

    return {
      type,
      preview,
    };
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  const fetchData = async () => {
    setShowLoading(true);

    await axios
      .get(`/portal/wacc/businesses/numbers/${ref}/message_pool.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          const data = result.data.data;

          setMessages(data.messages || []);
          setAgents(data.agents || []);
          setPageLoaded(true);
        } else {
          statusDialog.show(result.data.data.message, "error", "Request Failed");
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Request Failed"
        );
      });
  };

  const handleAgentChange = (messageReference, agentReference) => {
    setSelectedAgents((previous) => ({
      ...previous,
      [messageReference]: agentReference,
    }));
  };

  const assignMessage = async (messageReference) => {
    const agentReference = selectedAgents[messageReference];

    if (!agentReference) {
      await statusDialog.show("Please select an agent.", "warning", "Assignment Required");
      return;
    }

    setAssigning(messageReference);
    setShowLoading(true);

    await axios
      .post("/portal/wacc/businesses/numbers/message_pool/assign.json", {
        reference: messageReference,
        agent: agentReference,
      })
      .then(async (result) => {
        setShowLoading(false);
        setAssigning(null);

        if (result.data.status === "success") {
          setMessages((previous) =>
            previous.filter((message) => message.reference !== messageReference)
          );

          setSelectedAgents((previous) => {
            const updated = { ...previous };
            delete updated[messageReference];
            return updated;
          });

          await statusDialog.show(
            result.data.data.message || "Message assigned successfully.",
            "success",
            "Assignment Successful"
          );
        } else {
          await statusDialog.show(result.data.data.message, "error", "Assignment Failed");
        }
      })
      .catch(async () => {
        setShowLoading(false);
        setAssigning(null);

        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Request Failed"
        );
      });
  };

  useEffect(() => {
    fetchData();
  }, [ref]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <MDBox mt={1} mb={5}>
        <MDBox mb={4} display="flex" justifyContent="space-between">
          <MDBox display="flex" alignItems="center" gap={2}>
            <MDButton
              variant="outlined"
              color="dark"
              iconOnly
              onClick={() => navigate(`/wacc/businesses/number/${ref}`)}
            >
              <Icon>west</Icon>
            </MDButton>
            <MDBox>
              <MDTypography variant="h5" fontWeight="medium">
                Incoming Message Pool
              </MDTypography>

              <MDTypography mt={0} variant="body2" color="text" fontSize={13}>
                {messages.length} unassigned message
                {messages.length === 1 ? "" : "s"}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox>
            <MDButton
              variant="outlined"
              color="dark"
              iconOnly
              onClick={fetchData}
            >
              <Icon>refresh</Icon>
            </MDButton>
          </MDBox>
        </MDBox>

        {pageLoaded && (
          <MDBox>

            {messages.length === 0 ? (
              <Card>
                <MDBox
                  p={5}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                >
                  <Icon
                    sx={{
                      fontSize: 45,
                      color: "text.secondary",
                      mb: 1,
                    }}
                  >
                    mark_chat_unread
                  </Icon>

                  <MDTypography variant="h6" fontWeight="medium">
                    No incoming messages
                  </MDTypography>

                  <MDTypography variant="button" color="text">
                    New unassigned messages will appear here.
                  </MDTypography>
                </MDBox>
              </Card>
            ) : (
              <Grid container spacing={2} alignItems="stretch">
                {messages.map((message) => {
                  const details = getMessageDetails(message.message_payload);

                  const contactName = message.contact?.name || "Unknown Contact";

                  const phoneNumber = message.contact?.phone_number || "-";

                  const isAssigning = assigning === message.reference;

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      lg={4}
                      key={message.reference}
                      sx={{ display: "flex" }}
                    >
                      <Card
                        sx={{
                          height: "100%",
                          minHeight: 280,
                          width: "100%",
                          borderRadius: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <MDBox
                          p={2}
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1.5}>
                            <Avatar
                              sx={{
                                width: 42,
                                height: 42,
                                bgcolor: "grey.200",
                                color: "text.primary",
                              }}
                            >
                              <Icon>person</Icon>
                            </Avatar>

                            <MDBox>
                              <MDTypography variant="button" fontWeight="medium">
                                {contactName}
                              </MDTypography>

                              <MDTypography
                                variant="body2"
                                color="text"
                                display="block"
                                fontSize={13}
                              >
                                {phoneNumber}
                              </MDTypography>

                              <MDTypography variant="body2" color="text" fontSize={13}>
                                {formatDate(message.created)}
                              </MDTypography>
                            </MDBox>
                          </MDBox>

                          <Divider sx={{ my: 2 }} />

                          <MDBox
                            sx={{
                              backgroundColor: "grey.100",
                              borderRadius: 1,
                              p: 2,
                              mb: 2,
                              flex: 1,
                              minHeight: 65,
                            }}
                          >
                            <MDTypography
                              variant="button"
                              color="text"
                              sx={{
                                display: "block",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {details.preview}
                            </MDTypography>
                          </MDBox>

                          {/* Assignment controls remain at the bottom */}
                          <MDBox sx={{ mt: "auto" }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} md={8}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    displayEmpty
                                    value={selectedAgents[message.reference] || ""}
                                    onChange={(event) =>
                                      handleAgentChange(message.reference, event.target.value)
                                    }
                                    disabled={isAssigning}
                                    sx={{
                                      padding: "6px",
                                      paddingLeft: "3px",
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      Select an agent
                                    </MenuItem>

                                    {agents.map((agent) => (
                                      <MenuItem key={agent.reference} value={agent.reference}>
                                        {agent.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <MDButton
                                  variant="contained"
                                  color="dark"
                                  fullWidth
                                  size="small"
                                  disabled={
                                    isAssigning ||
                                    agents.length === 0 ||
                                    !selectedAgents[message.reference]
                                  }
                                  startIcon={
                                    isAssigning ? (
                                      <CircularProgress size={15} color="inherit" />
                                    ) : (
                                      <Icon>person_add</Icon>
                                    )
                                  }
                                  onClick={() => assignMessage(message.reference)}
                                >
                                  Assign
                                </MDButton>
                              </Grid>
                            </Grid>

                            {agents.length === 0 && (
                              <MDTypography variant="caption" color="error" display="block" mt={1}>
                                No active agents are assigned to this WhatsApp number.
                              </MDTypography>
                            )}
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsappMessagePoolPage;
